<template>
  <Teleport to="body">
    <div v-if="show">
      <!-- Dark Background Overlay -->
      <div
        class="tw-fixed tw-inset-0 tw-bg-black tw-opacity-50 tw-z-[99]"
        @click="onClickCloseButton"
      />

      <!-- Modal Content -->
      <div
        :class="[
          'tw-fixed tw-left-[50%] tw-transform tw-translate-x-[-50%] tw-z-[100] tw-bg-white tw-pl-8 tw-pr-8 tw-pb-8 tw-pt-[64px] apDesktop:tw-rounded-[20px]',
          'tw-flex tw-flex-col tw-w-full',
          'apDesktop:tw-top-[50%] apDesktop:tw-translate-y-[-50%] apDesktop:tw-max-w-[544px]',
          'tw-bottom-0 tw-translate-y-0 apDesktop:tw-bottom-auto',
          'tw-h-[80vh] apDesktop:tw-h-auto',
          'tw-rounded-tl-[20px] tw-rounded-tr-[20px]',
        ]"
      >
        <div class="tw-flex tw-justify-end tw-w-full tw-absolute tw-top-5 tw-right-5">
          <img class="hover:tw-opacity-50 tw-cursor-pointer tw-h-10 tw-w-10" :src="$cdn('icons/contact-us/icon_close_dark.svg')" alt="close_icon" @click="onClickCloseButton">
        </div>
        <div class="tw-text-h3-bold-mobile apTablet:tw-text-h4-bold-desktop tw-w-full">
          Manage cookies
        </div>
        <section class="tw-py-10 tw-flex tw-flex-col apDesktop:tw-gap-8 tw-gap-6 tw-w-full">
          <div v-for="item in settings" :key="item.title" class="tw-flex tw-justify-between tw-gap-4">
            <div>
              <div class="tw-text-body-bold-mobile apDesktop:tw-text-body-bold-desktop">
                {{ item.title }}
              </div>
              <div class="tw-text-label-md-mobile apDesktop:tw-text-label-md-desktop tw-text-ap-nobel">
                {{ item.subtitle }}
              </div>
            </div>
            <button class="tw-min-w-[58px]">
              <img
                :class="[
                  'tw-transition-all tw-duration-300',
                  { 'tw-opacity-50': item.isLocked },
                ]"
                :src="$cdn(!item.value ? 'icons/shared/icon_disable_toggle.png' : 'icons/shared/icon_enable_toggle.png')"
                alt=""
                @click="toggleValue(item)"
              >
            </button>
          </div>
        </section>

        <!-- Spacer with flex-grow -->
        <div class="tw-flex-grow apDesktop:tw-hidden" />

        <!-- Buttons Section -->
        <section class="tw-flex tw-justify-end tw-w-full apDesktop:tw-gap-4 tw-gap-3 tw-flex-col-reverse apDesktop:tw-flex-row">
          <button
            :class="[
              'tw-bg-white tw-text-ap-nero tw-py-4 tw-px-6 tw-rounded-xl',
              'tw-outline tw-outline-ap-gainsboro tw-outline-offset-[-2px] tw-outline-1',
              'tw-cursor-pointer tw-text-nowrap',
              'tw-text-body-md-mobile apDesktop:tw-text-body-md-desktop'
            ]"
            @click="onClickCancelButton"
          >Cancel</button>
          <button
            :class="[
              'tw-bg-ap-dodger-blue tw-text-white tw-py-4 tw-px-6 tw-rounded-xl',
              'tw-cursor-pointer tw-text-nowrap',
              'tw-text-body-md-mobile apDesktop:tw-text-body-md-desktop'
            ]"
            @click="onClickAllowSelection"
          >Allow selection</button>
        </section>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
});

watch(() => props.show, (value) => {
  if (value) {
    lockScroll();
  }else{
    unlockScroll();
  }
});

const emit = defineEmits(['update:show']);

function onClickCloseButton() {
  emit('update:show', false);
}

function onClickCancelButton() {
  emit('update:show', false);
}

function onClickAllowSelection() {
  savePreferencesToLocalStorage();
  emit('update:show', false);
}

const toggleValue = (item: CookieSetting) => {
  if (item.isLocked) {
    return;
  }
  item.value = !item.value;
};

// Lock scroll when modal is active
const lockScroll = () => {
  document.body.style.overflow = 'hidden';
};

// Unlock scroll when modal is inactive
const unlockScroll = () => {
  document.body.style.overflow = '';
};

interface CookieSetting {
  key: string,
  title: string,
  subtitle: string,
  value: boolean,
  isLocked: boolean
};

const settings = ref<CookieSetting[]>([
  {
    key: 'essential',
    title: 'Essential',
    subtitle: 'These cookies are necessary to provide you with services available through our website.',
    value: true,
    isLocked: true
  },
  {
    key: 'performance',
    title: 'Performance',
    subtitle: 'We use cookies to improve your experience. By using our site, you agree to our use of cookies.',
    value: true,
    isLocked: false
  },
  {
    key: 'analytics',
    title: 'Analytics',
    subtitle: 'These cookies analyse website usage, track marketing effectiveness, and customise our website for you.',
    value: false,
    isLocked: false
  },
  {
    key: 'social_networking',
    title: 'Social networking',
    subtitle: 'These cookies allow you to share pages and content from our website to social networks.',
    value: false,
    isLocked: false
  },
]);

const savePreferencesToLocalStorage = () => {
  const preferences = settings.value.reduce((acc, setting) => {
    acc[setting.key] = setting.value;
    return acc;
  }, {} as Record<string, boolean>);

  localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
};


</script>

<style scoped>
</style>